// Career coach listings — edit PARTNERS array. Images: public/images/partners/{filename}

import { useState } from "react";
import { ExternalLink, ArrowRight, Users } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Partner {
  name: string;
  description: string;
  url: string;
  specialty: string;
  /** Filename in /images/partners/ e.g. "jane-smith.jpg" */
  image: string;
}

// ============================================================
// EDIT THIS ARRAY TO ADD/REMOVE PARTNERS
// Images: public/images/partners/ (served as /images/partners/...)
// ============================================================
const PARTNERS: Partner[] = [
  {
  name: "Joyce M.",
    description: "An example listing for a Jobily career coach.",
    url: "https://www.facebook.com/people/Jobily/61574347436215/",
    specialty: "Jobily",
    image: "jobilyholder.png",
  },
];

function partnerInitials(name: string): string {
  return name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function PartnerAvatar({ name, image }: { name: string; image: string }) {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <div className="w-14 h-14 rounded-xl shrink-0 bg-muted flex items-center justify-center border border-border">
        <span className="text-lg font-bold text-muted-foreground">
          {partnerInitials(name)}
        </span>
      </div>
    );
  }

  return (
    <div className="w-14 h-14 rounded-xl overflow-hidden shrink-0 bg-muted border border-border">
      <img
        src={`/images/partners/${image}`}
        alt=""
        className="w-full h-full object-cover"
        onError={() => setFailed(true)}
      />
    </div>
  );
}

export default function PartnersTab() {
  return (
    <div className="space-y-6 animate-fade-up">
      <div>
        <h3 className="font-display font-bold text-lg text-foreground">
          Career Coaches
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          Trusted professionals who can help you navigate your job search,
          prepare for interviews, and negotiate offers. We only list coaches we
          trust.
        </p>
      </div>

      {PARTNERS.length > 0 ? (
        <div className="space-y-3">
          {PARTNERS.map((partner) => (
            <div
              key={partner.name}
              className="rounded-xl border border-border bg-card p-4 flex items-start gap-4"
            >
              <PartnerAvatar name={partner.name} image={partner.image} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="font-display font-semibold text-sm text-foreground">
                    {partner.name}
                  </h4>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 font-medium">
                    {partner.specialty}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  {partner.description}
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="shrink-0 gap-1 text-xs h-8"
                onClick={() => window.open(partner.url, "_blank")}
              >
                <ExternalLink className="w-3 h-3" />
                Visit
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-xl border-2 border-dashed border-border p-8 text-center space-y-3">
          <Users className="w-10 h-10 mx-auto text-muted-foreground/30" />
          <div>
            <h4 className="font-display font-semibold text-sm text-foreground">
              Coach listings coming soon
            </h4>
            <p className="text-xs text-muted-foreground mt-1 max-w-sm mx-auto">
              We&apos;re connecting with trusted career coaches. Check back soon
              for vetted partners.
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5 text-xs"
            onClick={() =>
              window.open(
                "https://www.facebook.com/people/Jobily/61574347436215/",
                "_blank"
              )
            }
          >
            Become a partner <ArrowRight className="w-3 h-3" />
          </Button>
        </div>
      )}

      <div className="rounded-lg bg-muted/50 border border-border p-3">
        <p className="text-xs text-muted-foreground">
          Are you a career coach interested in partnering with Jobily?{" "}
          <a
            href="https://www.facebook.com/people/Jobily/61574347436215/"
            className="text-primary hover:underline font-medium"
          >
            Get in touch
          </a>
        </p>
      </div>

      <div className="rounded-lg bg-muted/30 p-3 space-y-1.5">
        <h4 className="text-[8px] font-semibold text-foreground uppercase tracking-wide">
          Disclaimer
        </h4>
        <div className="text-[8px] text-muted-foreground leading-relaxed space-y-1.5">
          <p>
            Coaches on this page are vetted in good faith, but individual
            results vary. Engaging a coach is your decision; we do not endorse
            or guarantee any particular outcome.
          </p>
          <p>
            Coaches operate as independent professionals. Jobily is not
            responsible for their advice, services, conduct, or any dispute
            that may arise between you and a coach.
          </p>
          <p>
            You may be charged fees by a coach for their services; those
            arrangements are solely between you and the coach.
          </p>
          <p>
            Listings are provided at no cost to featured coaches. Jobily does
            not accept payment to place coaches here, and these listings are
            not sponsored advertisements.
          </p>
        </div>
      </div>
    </div>
  );
}
